import type { DepthConfidence } from "./snow-depth-validation";

export type SkiDepthGatedSport = "piste" | "off-piste" | "touring";

export type SkiScoreConfidenceRow = Readonly<{
  score_provenance?: unknown;
}>;

export type SkiScoreConfidenceMetadata = Readonly<{
  confidence: DepthConfidence;
  reason: string | null;
  lo: number | null;
  hi: number | null;
  depthGate: "pass" | "marginal" | "fail" | "unknown" | null;
  suppressionApplied: string | null;
  basis?: string | null;
  rangeKind?: SkiScoreRangeKind | null;
}>;

/** Scenario envelope (revision 2) or member P10 to P90 (revision 3). */
export type SkiScoreRangeKind = "scenario-envelope" | "p10-p90";
/** Evidence class (revision 3 stores it as `cls`; revision 2 derives it from `c`). */
export type SkiScoreConfidenceClass = "A" | "B" | "C";
/** Stored status vocabulary of revision-3 provenance (consumer inventory 6.1). */
export type SkiScoreStoredStatus = "open" | "closed" | "unknown" | "out-of-season";
export type SkiScoreStatus = Readonly<{
  status: SkiScoreStoredStatus;
  reason: string | null;
}>;
export type SkiScoreRange = Readonly<{
  /** 0 to 100 integers. */
  lo: number;
  hi: number;
  kind: SkiScoreRangeKind;
}>;

const CONFIDENCE_VALUES = new Set<DepthConfidence>([
  "full",
  "partial",
  "inferred",
  "unavailable",
]);
const RANGE_KINDS = new Set<SkiScoreRangeKind>(["scenario-envelope", "p10-p90"]);
const CLASSES = new Set<SkiScoreConfidenceClass>(["A", "B", "C"]);
const STATUSES = new Set<SkiScoreStoredStatus>(["open", "closed", "unknown", "out-of-season"]);
const DEPTH_GATES = new Set([
  "pass",
  "marginal",
  "fail",
  "unknown",
]);

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function finiteInteger(value: unknown): number | null {
  return typeof value === "number" &&
      Number.isFinite(value) &&
      Number.isInteger(value)
    ? value
    : null;
}

/** The per-sport provenance block: the saved-input rescore replacement when present. */
function sportBlock(
  row: SkiScoreConfidenceRow | null | undefined,
  sport: SkiDepthGatedSport,
): Readonly<{ block: Record<string, unknown>; replaced: boolean }> {
  const provenance = record(row?.score_provenance);
  const recalculated = record(provenance["recalculatedSports"]);
  if (Object.hasOwn(recalculated, sport)) {
    return { block: record(record(recalculated[sport])["snow"]), replaced: true };
  }
  return { block: record(record(record(provenance["depth"])["sports"])[sport]), replaced: false };
}

/**
 * True when a revision-3 block's stored status withholds the sport's score (Closed, out of
 * season, or no lift access for piste and off-piste; the rule of ski-labels-v3.ts
 * skiStatusWithholds). Such a block's range is never read: new rows store it as null, and
 * older rows still carry the "if open" P10 and P90. Revision-2 blocks (no `cls`) never match.
 */
function withheldBlock(block: Record<string, unknown>, sport: SkiDepthGatedSport): boolean {
  const cls = block["cls"];
  if (typeof cls !== "string" || !CLASSES.has(cls as SkiScoreConfidenceClass)) return false;
  const status = block["status"];
  if (status === "no_lift_access" || block["statusReason"] === "no-lift-access") return sport !== "touring";
  return status === "closed" || status === "out-of-season";
}

function metadata(
  row: SkiScoreConfidenceRow | null | undefined,
  sport?: SkiDepthGatedSport,
): SkiScoreConfidenceMetadata {
  const provenance = record(row?.score_provenance);
  const depth = record(provenance["depth"]);
  const rawConfidence = depth["c"];
  const confidence = typeof rawConfidence === "string" &&
      CONFIDENCE_VALUES.has(rawConfidence as DepthConfidence)
    ? rawConfidence as DepthConfidence
    : "unavailable";
  const sports = record(depth["sports"]);
  const recalculated = record(provenance["recalculatedSports"]);
  const replacement = sport && Object.hasOwn(recalculated, sport) ? record(recalculated[sport]) : null;
  const sportMetadata = replacement ? record(replacement["snow"]) : sport ? record(sports[sport]) : {};
  const rawSportConfidence = sportMetadata["c"];
  const sportConfidence = typeof rawSportConfidence === "string" &&
      CONFIDENCE_VALUES.has(rawSportConfidence as DepthConfidence)
    ? rawSportConfidence as DepthConfidence
    : replacement ? "unavailable" : confidence;
  const rawGate = sportMetadata["depthGate"];
  const reason = replacement ? replacement["snowReason"] : depth["reason"];
  const rangeWithheld = sport !== undefined && withheldBlock(sportMetadata, sport);
  return {
    ...(typeof sportMetadata["basis"] === "string" ? { basis: sportMetadata["basis"] } : {}),
    ...(typeof sportMetadata["rangeKind"] === "string" && RANGE_KINDS.has(sportMetadata["rangeKind"] as SkiScoreRangeKind)
      ? { rangeKind: sportMetadata["rangeKind"] as SkiScoreRangeKind }
      : {}),
    confidence: sportConfidence,
    reason: sportConfidence === "full"
      ? null
      : typeof reason === "string"
      ? reason
      : sportConfidence === "unavailable"
        ? "missing-provenance"
        : null,
    lo: rangeWithheld ? null : finiteInteger(sportMetadata["lo"]),
    hi: rangeWithheld ? null : finiteInteger(sportMetadata["hi"]),
    depthGate: typeof rawGate === "string" && DEPTH_GATES.has(rawGate)
      ? rawGate as SkiScoreConfidenceMetadata["depthGate"]
      : null,
    suppressionApplied:
      typeof sportMetadata["suppressionApplied"] === "string"
        ? sportMetadata["suppressionApplied"]
        : null,
  };
}

/**
 * Sole canonical reader for stored ski depth confidence.
 *
 * Missing or malformed pre-contract provenance fails closed. Callers must not
 * infer full confidence from a score column or scoring version.
 */
export function skiScoreConfidence(
  row: SkiScoreConfidenceRow | null | undefined,
): DepthConfidence {
  return metadata(row).confidence;
}

export function skiScoreConfidenceMetadata(
  row: SkiScoreConfidenceRow | null | undefined,
  sport: SkiDepthGatedSport,
): SkiScoreConfidenceMetadata {
  return metadata(row, sport);
}

export function skiScoreConfidenceIsPublishable(
  confidence: DepthConfidence | null | undefined,
): boolean {
  return confidence === "full" || confidence === "partial" || confidence === "inferred";
}

export function skiSportScoreIsPublishable(
  row: SkiScoreConfidenceRow | null | undefined,
  sport: SkiDepthGatedSport,
): boolean {
  return skiScoreConfidenceIsPublishable(
    skiScoreConfidenceMetadata(row, sport).confidence,
  );
}

export function leastSkiScoreConfidence(
  rows: readonly SkiScoreConfidenceRow[],
  sport?: SkiDepthGatedSport,
): DepthConfidence {
  const rank: Record<DepthConfidence, number> = {
    full: 0,
    partial: 1,
    inferred: 2,
    unavailable: 3,
  };
  return rows.reduce<DepthConfidence>((least, row) => {
    const next = sport
      ? skiScoreConfidenceMetadata(row, sport).confidence
      : skiScoreConfidence(row);
    return rank[next] > rank[least] ? next : least;
  }, "full");
}

/**
 * The evidence class the v3 engine stored for one sport (`cls`, or the
 * saved-input rescore replacement's), with no derivation. Null on revision-2
 * rows, which carry only the legacy confidence, and on malformed values. Gates
 * that must keep their pre-v3 rule on revision-2 rows use this to tell the two
 * apart (lib/conditions/evidence-policy.ts, SKI_LEGACY_EVIDENCE_POLICY).
 */
export function skiScoreStoredConfidenceClass(
  row: SkiScoreConfidenceRow | null | undefined,
  sport: SkiDepthGatedSport,
): SkiScoreConfidenceClass | null {
  const stored = sportBlock(row, sport).block["cls"];
  return typeof stored === "string" && CLASSES.has(stored as SkiScoreConfidenceClass)
    ? stored as SkiScoreConfidenceClass
    : null;
}

/**
 * Evidence class for one sport: the stored revision-3 `cls` (or the saved-input
 * rescore replacement's), else derived from the legacy confidence
 * (full A, partial B, inferred C). Null when the provenance is missing or
 * malformed; callers must never infer a class from a score or a version.
 */
export function skiScoreConfidenceClass(
  row: SkiScoreConfidenceRow | null | undefined,
  sport: SkiDepthGatedSport,
): SkiScoreConfidenceClass | null {
  const stored = skiScoreStoredConfidenceClass(row, sport);
  if (stored !== null) return stored;
  switch (skiScoreConfidenceMetadata(row, sport).confidence) {
    case "full":
      return "A";
    case "partial":
      return "B";
    case "inferred":
      return "C";
    default:
      return null;
  }
}

/**
 * Stored status for one sport (revision 3 only): "closed" means the published
 * score is withheld and reads "Closed". Null for revision-2 rows, which carry
 * no status and were always published as open.
 */
export function skiScoreStatus(
  row: SkiScoreConfidenceRow | null | undefined,
  sport: SkiDepthGatedSport,
): SkiScoreStatus | null {
  const block = sportBlock(row, sport).block;
  const status = block["status"];
  if (typeof status !== "string" || !STATUSES.has(status as SkiScoreStoredStatus)) return null;
  const reason = block["statusReason"];
  return { status: status as SkiScoreStoredStatus, reason: typeof reason === "string" ? reason : null };
}

/**
 * Stored score range for one sport on the 0 to 100 scale: the revision-3 P10
 * and P90 when present, else the stored `lo`/`hi` with their range kind
 * (a revision-2 scenario envelope when unlabelled). Null when either bound is
 * missing, not an integer or out of order, and whenever the revision-3 status
 * withholds the score (a Closed sport has no range).
 */
export function skiScoreRange(
  row: SkiScoreConfidenceRow | null | undefined,
  sport: SkiDepthGatedSport,
): SkiScoreRange | null {
  const block = sportBlock(row, sport).block;
  if (withheldBlock(block, sport)) return null;
  const inRange = (value: number | null): value is number => value !== null && value >= 0 && value <= 100;
  const p10 = finiteInteger(block["p10"]);
  const p90 = finiteInteger(block["p90"]);
  if (inRange(p10) && inRange(p90) && p10 <= p90) return { lo: p10, hi: p90, kind: "p10-p90" };
  const lo = finiteInteger(block["lo"]);
  const hi = finiteInteger(block["hi"]);
  if (!inRange(lo) || !inRange(hi) || lo > hi) return null;
  const kind = block["rangeKind"];
  return {
    lo,
    hi,
    kind: typeof kind === "string" && RANGE_KINDS.has(kind as SkiScoreRangeKind) ? kind as SkiScoreRangeKind : "scenario-envelope",
  };
}
